<template>
  <div class="container">
    <div class="header">
      <h1>电子画板</h1>
    </div>

    <div class="signature-container">
      <div class="flex Flexalign">
        <div class="signature-pad-wrapper">
          <canvas ref="canvas" class="signature-pad" :width="width" :height="height" @mousedown="startDrawing"
            @mousemove="draw" @mouseup="stopDrawing" @mouseleave="stopDrawing" @touchstart="startDrawingTouch"
            @touchmove="drawTouch" @touchend="stopDrawing"></canvas>

          <div v-if="isLocked" class="locked-message">
            画板已锁定
          </div>
        </div>

        <div class="toolbar flex flexcolumn ml-20">
          <button class="btn" @click="undo">
            <i class="fas fa-undo"></i> 撤销
          </button>

          <button class="btn btn-danger" @click="clear">
            <i class="fas fa-trash"></i> 清屏
          </button>

          <!-- <button class="btn btn-success" @click="save">
            <i class="fas fa-save"></i> 保存
          </button> -->

          <button class="btn btn-warning" @click="toggleEraser">
            <i class="fas fa-eraser"></i> {{ isEraser ? '画笔' : '橡皮擦' }}
          </button>

          <button class="btn" @click="toggleLock">
            <i class="fas fa-lock"></i> {{ isLocked ? '解锁' : '锁定' }}
          </button>
        </div>
      </div>

      <div class="options-panel">
        <div class="option-group">
          <span class="option-label">笔刷粗细: {{ brushSize }}px</span>
          <div class="slider-container">
            <i class="fas fa-minus"></i>
            <input type="range" min="1" max="20" v-model="brushSize">
            <i class="fas fa-plus"></i>
          </div>
        </div>

        <div class="option-group">
          <span class="option-label">画笔颜色</span>
          <div class="color-picker">
            <div v-for="color in colors" :key="color" class="color-option" :class="{ active: selectedColor === color }"
              :style="{ backgroundColor: color }" @click="selectColor(color)"></div>
          </div>
        </div>
      </div>

      <!-- <div class="preview-container" v-if="savedImage">
        <div class="preview-title">签名预览</div>
        <img :src="savedImage" class="signature-preview" alt="签名预览">
      </div> -->

      <div class="status-bar">
        状态: {{ isLocked ? '已锁定' : '已解锁' }} | 模式: {{ isEraser ? '橡皮擦' : '画笔' }} | 颜色: {{ selectedColor }} | 粗细: {{
          brushSize }}px
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SignaturePad',
  data() {
    return {
      isLocked: false,
      isEraser: false,
      isDrawing: false,
      brushSize: 3,
      selectedColor: '#000000',
      savedImage: null,
      savedImageUrl: null,
      width: 800,
      height: 550,
      colors: [
        '#000000', '#e74c3c', '#3498db', '#2ecc71',
        '#f39c12', '#9b59b6', '#34495e', '#ffffff'
      ],
      drawingHistory: [],
      currentPath: []
    }
  },
  mounted() {
    this.initCanvas();
    this.resizeCanvas(); // 初始调整尺寸
    window.addEventListener('resize', this.resizeCanvas);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.resizeCanvas);
  },
  watch: {
    brushSize() {
      this.updateCanvasStyle();
    },
    selectedColor() {
      this.updateCanvasStyle();
    },
    isEraser() {
      this.updateCanvasStyle();
    }
  },
  methods: {
    initCanvas() {
      this.ctx = this.$refs.canvas.getContext('2d');

      // 设置 Canvas 元素的实际宽度和高度
      this.$refs.canvas.width = this.width;
      this.$refs.canvas.height = this.height;

      this.updateCanvasStyle();
      this.clear();
    },
    updateCanvasStyle() {
      if (this.isEraser) {
        this.ctx.strokeStyle = '#FFFFFF';
        this.ctx.lineWidth = this.brushSize * 2;
      } else {
        this.ctx.strokeStyle = this.selectedColor;
        this.ctx.lineWidth = this.brushSize;
      }
      this.ctx.lineCap = 'round';
      this.ctx.lineJoin = 'round';
    },
    resizeCanvas() {
      const container = this.$refs.canvas.parentElement;
      if (container) {
        // 获取容器的实际显示尺寸
        const displayWidth = container.clientWidth - 4;
        const displayHeight = container.clientHeight - 4;

        // 设置 Canvas 的显示尺寸
        this.$refs.canvas.style.width = displayWidth + 'px';
        this.$refs.canvas.style.height = displayHeight + 'px';

        // 设置 Canvas 的坐标系统尺寸与显示尺寸一致
        this.width = displayWidth;
        this.height = displayHeight;

        // 重新初始化画布
        this.initCanvas();
        this.redrawCanvas();
      }
    },
    startDrawing(e) {
      if (this.isLocked) return;

      this.isDrawing = true;
      const rect = this.$refs.canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      this.currentPath = [{ x, y }];
      this.ctx.beginPath();
      this.ctx.moveTo(x, y);
    },
    draw(e) {
      if (!this.isDrawing || this.isLocked) return;

      const rect = this.$refs.canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      this.currentPath.push({ x, y });
      this.ctx.lineTo(x, y);
      this.ctx.stroke();
    },
    startDrawingTouch(e) {
      if (this.isLocked) return;

      e.preventDefault();
      this.isDrawing = true;
      const rect = this.$refs.canvas.getBoundingClientRect();
      const touch = e.touches[0];
      const x = touch.clientX - rect.left;
      const y = touch.clientY - rect.top;

      this.currentPath = [{ x, y }];
      this.ctx.beginPath();
      this.ctx.moveTo(x, y);
    },
    drawTouch(e) {
      if (!this.isDrawing || this.isLocked) return;

      e.preventDefault();
      const rect = this.$refs.canvas.getBoundingClientRect();
      const touch = e.touches[0];
      const x = touch.clientX - rect.left;
      const y = touch.clientY - rect.top;

      this.currentPath.push({ x, y });
      this.ctx.lineTo(x, y);
      this.ctx.stroke();
    },
    stopDrawing() {
      if (this.isDrawing) {
        this.isDrawing = false;
        if (this.currentPath.length > 0) {
          this.drawingHistory.push({
            path: [...this.currentPath],
            color: this.ctx.strokeStyle,
            width: this.ctx.lineWidth
          });
        }
        this.currentPath = [];
      }
    },
    undo() {
      if (this.isLocked || this.drawingHistory.length === 0) return;

      this.drawingHistory.pop();
      this.redrawCanvas();
    },
    clear() {
      if (this.isLocked) return;

      this.ctx.fillStyle = '#FFFFFF';
      this.ctx.fillRect(0, 0, this.width, this.height);
      this.drawingHistory = [];
      this.currentPath = [];
    },
    save() {
      if (this.isLocked) return;

      this.savedImage = this.$refs.canvas.toDataURL('image/png');
      let blob = this.$Tools.base64ToBlob(this.savedImage);
      // console.log(this.savedImage);
      this.savedImageUrl = URL.createObjectURL(blob);
    },
    toggleEraser() {
      if (this.isLocked) return;
      this.isEraser = !this.isEraser;
    },
    toggleLock() {
      this.isLocked = !this.isLocked;
    },
    selectColor(color) {
      if (this.isLocked) return;
      this.selectedColor = color;
      this.isEraser = false;
    },
    redrawCanvas() {
      // 清除画布
      this.ctx.fillStyle = '#FFFFFF';
      this.ctx.fillRect(0, 0, this.width, this.height);

      // 重新绘制历史记录
      this.drawingHistory.forEach(item => {
        this.ctx.strokeStyle = item.color;
        this.ctx.lineWidth = item.width;
        this.ctx.beginPath();

        if (item.path.length > 0) {
          this.ctx.moveTo(item.path[0].x, item.path[0].y);
          for (let i = 1; i < item.path.length; i++) {
            this.ctx.lineTo(item.path[i].x, item.path[i].y);
          }
          this.ctx.stroke();
        }
      });

      // 恢复当前设置
      this.updateCanvasStyle();
    }
  }
}
</script>

<style lang="scss" scoped>
.container {
  width: 100%;
  max-width: 1100px;
  background: white;
  /* border-radius: 16px; */
  /* box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15); */
  overflow: hidden;
  // padding: 20px;
  margin: 0 auto;
}

.header {
  text-align: center;
  margin-bottom: 20px;
}

.header h1 {
  font-size: 28px;
  font-weight: 600;
  color: #2c3e50;
}

.signature-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.signature-pad-wrapper {
  border: 2px dashed #3498db;
  border-radius: 8px;
  background: #f8f9fa;
  position: relative;
  height: 550px;
}

.signature-pad {
  width: 100%;
  height: 100%;
  background: white;
  cursor: crosshair;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
  padding: 15px;
  background: #ecf0f1;
  border-radius: 8px;
}

.btn {
  padding: 10px 15px;
  border: none;
  border-radius: 6px;
  background: #3498db;
  color: white;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 5px;
}

.btn:hover {
  background: #2980b9;
  transform: translateY(-2px);
}

.btn-danger {
  background: #e74c3c;
}

.btn-danger:hover {
  background: #c0392b;
}

.btn-success {
  background: #2ecc71;
}

.btn-success:hover {
  background: #27ae60;
}

.btn-warning {
  background: #f39c12;
}

.btn-warning:hover {
  background: #d35400;
}

.options-panel {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  justify-content: center;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
}

.option-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.option-label {
  font-weight: 500;
  color: #2c3e50;
}

.slider-container {
  display: flex;
  align-items: center;
  gap: 10px;
}

input[type="range"] {
  width: 150px;
}

.color-picker {
  display: flex;
  gap: 5px;
}

.color-option {
  width: 25px;
  height: 25px;
  border-radius: 50%;
  cursor: pointer;
  border: 2px solid transparent;
  transition: transform 0.2s;
}

.color-option:hover,
.color-option.active {
  transform: scale(1.1);
  border-color: #2c3e50;
}

.preview-container {
  margin-top: 20px;
  text-align: center;
}

.preview-title {
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 10px;
  color: #2c3e50;
}

.signature-preview {
  max-width: 100%;
  max-height: 200px;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 10px;
  background: white;
}

.status-bar {
  // margin-top: 15px;
  padding: 10px;
  background: #2c3e50;
  color: white;
  border-radius: 6px;
  text-align: center;
  font-weight: 500;
}

.locked-message {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 24px;
  font-weight: bold;
  border-radius: 8px;
  z-index: 10;
  pointer-events: none;
}
</style>

<style>
/* 全局样式，用于图标 */
@import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css');
</style>